/**
 * Rapor üretimi: ağırlıklı skor, kategori kırılımı, satır bazlı annotasyon.
 *
 * Skor kuralı: yalnızca "decisive" ve confidence ≥ medium olan issue'lar
 * cezalandırılır. Düşük güvenli bulgular kullanıcıya gösterilir ama karara
 * katılmaz; böylece "PDF parse edemedi diye makaleyi suçlama" ilkesi korunur.
 */

import type {
	AnalyzeOptions,
	CategoryScore,
	Confidence,
	ExtractedDocument,
	Issue,
	IssueCategory,
	Report,
	Severity
} from '../types';
import { DocumentIndex } from '../text/document';
import { confidenceWeight } from '../text/normalize';
import { runStatsEngine, clampScore } from '../stats/engine';
import { runReferenceEngine } from '../references/engine';
import { runStructureEngine } from '../structure/engine';
import { runLanguageEngine } from '../language/engine';
import { runAccessEngine } from '../engines/access/engine';
import { runGuidelinesEngine } from '../engines/guidelines/engine';

export const DEFAULT_WEIGHTS: Record<IssueCategory, number> = {
	statistics: 0.33,
	citations: 0.24,
	structure: 0.19,
	language: 0.14,
	provenance: 0.05,
	access: 0.05
};

export const CATEGORY_LABELS: Record<IssueCategory, string> = {
	statistics: 'İstatistiksel raporlama',
	citations: 'Atıf ve kaynakça',
	structure: 'Yapısal bütünlük',
	language: 'Dil ve iddia düzeyi',
	provenance: 'Çıkarım güvenilirliği',
	access: 'Erişim ve şeffaflık'
};

/** Severity başına taban ceza. */
const SEVERITY_PENALTY: Record<Severity, number> = {
	critical: 16,
	warning: 7,
	info: 2.5,
	ok: 0
};

export interface AnalysisResult {
	report: Report;
	index: DocumentIndex;
}

export function analyzeDocument(
	doc: ExtractedDocument,
	options: AnalyzeOptions = {}
): AnalysisResult {
	const index = new DocumentIndex(doc);

	const stats = runStatsEngine(index, {
		alpha: options.alpha,
		tolerance: options.pTolerance
	});
	const citations = runReferenceEngine(index);
	const structure = runStructureEngine(index);
	const language = runLanguageEngine(index);
	const access = runAccessEngine(index, doc.meta);
	const guidelines = runGuidelinesEngine(index);

	// Provenans bulguları
	const provenanceIssues = buildProvenanceIssues(doc);

	const allIssues: Issue[] = dedupeIssues([
		...stats.issues,
		...citations.issues,
		...structure.issues,
		...language.issues,
		...access.issues,
		...guidelines.issues,
		...provenanceIssues
	]);

	// Çıkarım kalitesi düşükse tüm bulguların güvenini bir kademe düşür
	const downgraded = doc.quality.level === 'low';
	if (downgraded) {
		for (const issue of allIssues) {
			if (issue.category === 'provenance') continue;
			issue.confidence = downgrade(issue.confidence);
			issue.decisive = issue.decisive && issue.confidence !== 'low';
			issue.detail +=
				' (Belge metin kalitesi düşük olduğu için bu bulgunun güveni bir kademe düşürüldü.)';
		}
	}

	// Karar verilebilirlik: decisive && confidence >= medium && >= 1 kanıt
	for (const issue of allIssues) {
		if (issue.confidence === 'low') issue.decisive = false;
	}

	const weights = { ...DEFAULT_WEIGHTS, ...(options.weights ?? {}) };
	const categories = buildCategoryScores(allIssues, weights, {
		statsChecked: stats.result.findings.some((f) => f.recomputedP !== undefined),
		citationsChecked:
			citations.result.matches.some((m) => m.status === 'matched') &&
			!citations.issues.some((i) => i.code === 'cite.no_reference_section'),
		structureChecked: structure.result.sections.length >= 2,
		languageChecked: language.result.hits.length > 0 || language.result.design !== 'unknown',
		accessChecked: doc.text.trim().length > 0
	});

	const checkedCategories = categories.filter((c) => c.checked);
	const checkedWeight = checkedCategories.reduce((sum, c) => sum + c.weight, 0);
	const overall = clampScore(
		checkedWeight > 0
			? checkedCategories.reduce((sum, c) => sum + c.score * c.weight, 0) / checkedWeight
			: 0
	);
	const coverage = {
		checked: checkedCategories.length,
		total: categories.length,
		ratio: categories.length ? checkedCategories.length / categories.length : 0,
		sufficient: categories.length > 0 && checkedCategories.length / categories.length >= 0.5
	};

	const counts: Record<Severity, number> = { critical: 0, warning: 0, info: 0, ok: 0 };
	for (const issue of allIssues) counts[issue.severity]++;

	const report: Report = {
		version: '1.0',
		generatedAt: new Date().toISOString(),
		document: {
			fileName: doc.fileName,
			format: doc.format,
			pages: doc.pageCount,
			chars: doc.text.length,
			quality: doc.quality
		},
		scores: {
			overall,
			grade: coverage.sufficient ? gradeOf(overall) : 'N/A',
			coverage,
			categories
		},
		stats: stats.result,
		citations: citations.result,
		structure: structure.result,
		language: language.result,
		access: access.result,
		guidelines: guidelines.result,
		issues: allIssues.sort(compareIssues),
		counts,
		undecided: allIssues.filter((i) => !i.decisive).length
	};

	return { report, index };
}

function buildCategoryScores(
	issues: Issue[],
	weights: Record<IssueCategory, number>,
	checked: {
		statsChecked: boolean;
		citationsChecked: boolean;
		structureChecked: boolean;
		languageChecked: boolean;
		accessChecked: boolean;
	}
): CategoryScore[] {
	const cats: IssueCategory[] = [
		'statistics',
		'citations',
		'structure',
		'language',
		'provenance',
		'access'
	];
	return cats.map((category) => {
		const catIssues = issues.filter((i) => i.category === category);
		const decisive = catIssues.filter((i) => i.decisive && i.confidence !== 'low');

		let score = 100;
		for (const issue of decisive) {
			const w = confidenceWeight(issue.confidence);
			score -= SEVERITY_PENALTY[issue.severity] * w;
		}
		// Aynı kodun çok sayıda tekrarı skoru sıfıra sürüklemesin: logaritmik tavan
		const count = decisive.length;
		if (count > 6) score += Math.min(25, (count - 6) * 2);

		let note: string | undefined;
		let isChecked = true;
		if (category === 'citations' && !checked.citationsChecked) {
			isChecked = false;
			score = 100;
			note = 'Atıf kontrolü yapılamadı (kaynakça/atıf eşleştirmesi güvenilir değil).';
		}
		if (category === 'statistics' && !checked.statsChecked) {
			isChecked = false;
			note = 'Raporlanmış istatistiklerden hiçbiri yeniden hesaplanarak doğrulanamadı.';
		}
		if (category === 'structure' && !checked.structureChecked) {
			isChecked = false;
			note = 'Bölüm yapısı çıkarılamadı.';
		}
		if (category === 'language' && !checked.languageChecked) {
			isChecked = false;
			score = 100;
		}
		if (category === 'access' && !checked.accessChecked) {
			isChecked = false;
			note = 'Erişim beyanları için yeterli metin yok.';
		}

		return {
			category,
			label: CATEGORY_LABELS[category],
			score: clampScore(score),
			weight: weights[category],
			contribution: clampScore(score) * weights[category],
			issueCount: catIssues.length,
			criticalCount: catIssues.filter((i) => i.severity === 'critical').length,
			checked: isChecked,
			note
		};
	});
}

export function dedupeIssues(issues: Issue[]): Issue[] {
	function sameSpan(a: Issue['location'], b: Issue['location']): boolean {
		if (a.blockId && b.blockId && a.blockId !== b.blockId) return false;
		if (a.page !== undefined && b.page !== undefined && a.page !== b.page) return false;
		if (a.charStart === undefined || b.charStart === undefined) return false;
		return Math.abs(a.charStart - b.charStart) < 20;
	}

	// Aynı kaynak bölgesinden (block + yakın charStart) gelen anlamlılık
	// iddialarında stats motorunun daha spesifik bulgusu tutulur.
	const statClaims = issues.filter((i) => i.code === 'stat.significance_claim');

	const seen = new Set<string>();
	const out: Issue[] = [];
	for (const issue of issues) {
		if (
			issue.code === 'lang.stat_claim_without_test' &&
			statClaims.some((s) => sameSpan(s.location, issue.location))
		) {
			continue;
		}
		const meta = issue.meta ?? {};
		const metaKey = [meta.sectionKey, meta.item, meta.check, meta.citation, meta.statement]
			.filter((v) => typeof v === 'string')
			.join(':');
		const key = [
			issue.code,
			issue.location.blockId ?? issue.location.page ?? '',
			issue.location.charStart ?? issue.location.sourceLine ?? '',
			(issue.quote ?? '').replace(/\s+/g, ' ').slice(0, 60),
			metaKey
		].join('|');
		if (seen.has(key)) continue;
		seen.add(key);
		out.push(issue);
	}
	return out;
}

function buildProvenanceIssues(doc: ExtractedDocument): Issue[] {	const issues: Issue[] = [];
	const q = doc.quality;

	if (q.level === 'low') {
		issues.push({
			id: 'prov-1',
			code: 'prov.low_text_quality',
			category: 'provenance',
			severity: 'warning',
			title: 'Metin çıkarımı güvenilir değil',
			detail:
				`Metin kalitesi skoru ${Math.round(q.score * 100)}/100. ` +
				(q.notes.join(' ') || 'Nedeni belirlenemedi.') +
				' Bu belgedeki bulgular manuel doğrulama gerektirir.',
			confidence: 'high',
			decisive: true,
			location: {},
			meta: { score: q.score, notes: q.notes.join(' ') }
		});
	}

	if (q.emptyPages > 0) {
		issues.push({
			id: 'prov-2',
			code: 'prov.scanned_pdf',
			category: 'provenance',
			severity: q.emptyPages / Math.max(1, q.pages) > 0.4 ? 'warning' : 'info',
			title: `${q.emptyPages} sayfada metin katmanı yok`,
			detail:
				'Taranmış sayfalar analiz edilemez. OCR uygulanmış bir sürüm yüklemeyi deneyin.',
			confidence: 'high',
			location: {},
			decisive: q.emptyPages / Math.max(1, q.pages) > 0.4,
			meta: { emptyPages: q.emptyPages, pages: q.pages }
		});
	}

	if (q.garbageRatio > 0.01) {
		issues.push({
			id: 'prov-3',
			code: 'prov.missing_glyphs',
			category: 'provenance',
			severity: q.garbageRatio > 0.05 ? 'warning' : 'info',
			title: 'Tanınmayan glifler',
			detail: `Metnin %${(q.garbageRatio * 100).toFixed(1)}'i okunamayan karakter içeriyor (font/kodlama sorunu).`,
			confidence: 'high',
			location: {},
			decisive: q.garbageRatio > 0.05,
			meta: { ratio: q.garbageRatio }
		});
	}

	if (q.chars < 500) {
		issues.push({
			id: 'prov-4',
			code: 'prov.short_document',
			category: 'provenance',
			severity: 'warning',
			title: 'Belge çok kısa',
			detail: 'Analiz için yeterli metin yok; sonuçlar temsil edici değildir.',
			confidence: 'high',
			decisive: true,
			location: {}
		});
	}

	return issues;
}

function compareIssues(a: Issue, b: Issue): number {
	const sev = { critical: 0, warning: 1, info: 2, ok: 3 } as const;
	const s = sev[a.severity] - sev[b.severity];
	if (s !== 0) return s;
	// Aynı severity içinde önce kararlı olanlar
	if (a.decisive !== b.decisive) return a.decisive ? -1 : 1;
	return (a.location.charStart ?? Number.MAX_SAFE_INTEGER) - (b.location.charStart ?? Number.MAX_SAFE_INTEGER);
}

function gradeOf(score: number): Report['scores']['grade'] {
	if (score >= 90) return 'A';
	if (score >= 75) return 'B';
	if (score >= 60) return 'C';
	return 'D';
}

function downgrade(c: Confidence): Confidence {
	return c === 'high' ? 'medium' : 'low';
}
